import React, { useState, useContext } from "react";
import { View, StyleSheet, ScrollView } from "react-native";
import colors from "../../config/colors";
import NtActivityIndicator from "../../components/NtActivityIndicator";
import UserContext from "../../context/UserContext";
import NtModalUnactiveButton from "../../components/NtModalUnactiveButton";
import NtModalActiveButton from "../../components/NtModalActiveButton";
import NtModalInputField from "../../components/NtModalInputField";
import { createExtension } from "../../api/extension";
import { parseError } from "@ntconnect/framework/pbx";
import NtModalError from "../../components/NtModalError";
import Logger from "../../common/utils/Logger";
import NtModalTitle from "../../components/NtModalTitle";
import NtModalSubtitle from "../../components/NtModalSubtitle";
import NtExtensionDescriptionInputField from "./NtExtensionDescriptionInputField";
import NtToggleRow from "../../components/NtToggleRow";

function NtExtensionNewForm({ onCancel, onCreate }) {
  const [extension, setExtension] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [description, setDescription] = useState("");
  const [tollAllow, setTollAllow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const context = useContext(UserContext);

  const isRequiredFields = () => {
    return name && extension && password;
  };

  const handleCreate = async () => {
    if (!isRequiredFields()) {
      return;
    }

    setLoading(true);
    setError(null);

    const params = {
      extension: extension,
      password: password,
      name: name,
      description: description,
      tollAllow: tollAllow,
    };

    const response = await createExtension(
      context.user?.domain,
      params,
    );

    Logger("Extension Created", response);

    if (response.ok) {
      setName("");
      setExtension("");
      setPassword("");
      setDescription("");
      setTollAllow(false);
      if (onCreate) {
        onCreate();
      }
    } else {
      setError(parseError(response));
    }

    setLoading(false);
  };

  return (
    <View style={styles.container}>
      <NtModalTitle>Add new Extension</NtModalTitle>
      <NtModalSubtitle style={[{ marginTop: 4 }]}>
        Use this to configure your SIP extensions.
      </NtModalSubtitle>

      <ScrollView
        scrollEnabled={true}
        contentContainerStyle={{ flexGrow: 1 }}
        showsVerticalScrollIndicator={false}
      >
        <NtModalInputField
          containerStyle={{ marginTop: 25 }}
          title={"Extension *"}
          placeholder={"e.g. 100"}
          value={extension}
          setValue={(text) => setExtension(text.replace(/\D/g, ""))}
          keyboardType="numeric"
          footer={
            "Common patterns: 100 (front desk), 1234 (cabin), 9001 (test). Use a number that isn't already in use."
          }
        />

        <NtModalInputField
          containerStyle={{ marginTop: 15 }}
          title={"Name *"}
          placeholder={'e.g. "Front Desk", "Bridge Office", "John Smith"'}
          value={name}
          setValue={(text) => setName(text)}
          footer={"Caller ID name shown on internal calls. Use a person's name or a role."}
        />

        <NtModalInputField
          containerStyle={{ marginTop: 15 }}
          title={"Password *"}
          placeholder={"SIP password"}
          value={password}
          setValue={(text) => setPassword(text)}
          footer={"Password the SIP device will use to register."}
        />

        <NtExtensionDescriptionInputField
          containerStyle={{ marginTop: 15 }}
          title={"Description"}
          value={description}
          setValue={(text) => setDescription(text)}
        />

        <View style={{ marginTop: 16 }}>
          <NtToggleRow
            value={tollAllow}
            onChange={setTollAllow}
            label="Allow outbound calls"
            sub="When off, this extension can only place internal calls."
          />
        </View>

        <NtModalError error={error} containerStyle={{ marginTop: 6 }} />
      </ScrollView>

      <View
        style={{
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "flex-end",
          marginTop: 25,
        }}
      >
        {loading && (
          <NtActivityIndicator
            color={colors.blue}
            size="small"
            containerStyle={{ marginRight: 25 }}
          />
        )}
        <NtModalUnactiveButton title={"Cancel"} onPress={onCancel} />
        <NtModalActiveButton
          containerStyle={{ marginLeft: 15 }}
          title={"Create"}
          onPress={() => {
            if (isRequiredFields()) {
              handleCreate();
            }
          }}
          enabled={isRequiredFields()}
        />
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    minWidth: 500,
    maxWidth: 600,
    padding: 15,
  },
});

export default NtExtensionNewForm;
